import React, { useContext, useEffect, useRef, useState } from 'react'
import { CachePolicies, useFetch } from 'use-http'
import { Box, Button, Flex, Link, Text as ChakraText } from '@chakra-ui/react'
import type fabric from 'fabric/fabric-impl'
import { UserContext } from '../../../providers'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import type { MediaLibrarySearchFileResponse } from '../../../interfaces'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import { getFileURL, isSignedURLRequired } from '../../../../utils/mediaLibrary'
import useImageKitClient from '../../../hooks/useImageKitClient'
import fabricUtils from './fabricUtils'
import CustomCoordinatesTable from './CustomCoordinatesTable'

interface Props {
  selectedFile: FileDetailsResponse | MediaLibrarySearchFileResponse
  onClose(): void
  getFileDetails(): Promise<FileDetailsResponse> | Promise<void>
}

interface ImageScaledInfo {
  isScaled: boolean
  scalingFactor: number
}

const CCEditor = ({
  selectedFile,
  onClose,
  getFileDetails,
}: Props): React.ReactElement => {
  const imagekit = useImageKitClient()
  const loadingText = useRef<fabric.Text>()
  const [isImageLoaded, setImageLoaded] = useState<boolean>()
  const [croppedImageUrl, setCroppedImageUrl] = useState('')
  const [isEditing, showEditOptions] = useState(false)
  const { user } = useContext(UserContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [imageUrl, setImageUrl] = useState<string>()

  const [changedCustomCoordinates, setChangedCustomCoordinates] = useState({
    'x-coordinate': 0,
    'y-coordinate': 0,
    width: 0,
    height: 0,
  })

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    user,
    clientInfo: undefined,
  })

  useEffect(() => {
    selectedFile.customCoordinates
      ? showEditOptions(true)
      : showEditOptions(false)

    return () => {
      showEditOptions(false)
    }
  }, [selectedFile.customCoordinates])

  const containerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)

  const rectObject = useRef<fabric.Rect>()
  const canvasRef = useRef<fabric.Canvas>()

  const preventCanvasCCDoubleUpdate = useRef(false)

  const emptyCCObj = {
    'x-coordinate': 0,
    'y-coordinate': 0,
    width: 0,
    height: 0,
  }

  const [canvasCC, setCC] = useState(emptyCCObj)
  const [initialCC, setInitialCC] = useState({
    'x-coordinate': 20,
    'y-coordinate': 20,
    width: 100,
    height: 100,
  })

  const [renderedBgImageHeight, setRenderedBgImageHeight] = useState<number>()
  const [renderedBgImageWidth, setRenderedBgImageWidth] = useState<number>()
  const [imageScaledInfo, setImageScaledInfo] = useState<ImageScaledInfo>()

  const getAndSetUrl = async (
    transformation: Array<{ [key: string]: string }> = [],
  ): Promise<string> => {
    let fileUrl = ''

    if (
      isSignedURLRequired({
        isPrivateFile: selectedFile.isPrivateFile,
        isPublished: selectedFile.isPublished,
        onlySignedGet: getFileUrlDynamicArguments.onlySignedGet,
        onlyNamedGet: getFileUrlDynamicArguments.onlyNamedGet,
      }) ||
      typeof getFileUrlDynamicArguments.firstCustomCname !== 'undefined'
    ) {
      fileUrl = await getFileURL({
        file: selectedFile,
        ...getFileUrlDynamicArguments,
        request,
        transformation,
      })
    } else {
      fileUrl = imagekit.url({
        src: selectedFile.url,
        transformation,
      })
    }
    return fileUrl
  }

  let canvas: fabric.Canvas
  let canvasWidth = 900
  const canvasHeight = 600

  const scalingFactor = useRef<number>(1)

  useEffect(() => {
    if (imageScaledInfo) {
      scalingFactor.current = imageScaledInfo.scalingFactor
    }
  }, [imageScaledInfo])

  useEffect(() => {
    const transformation: Record<string, string> = {}
    if (selectedFile.height) {
      transformation.height = selectedFile.height.toString()
    }
    if (selectedFile.width) {
      transformation.width = selectedFile.width.toString()
    }
    if (Object.keys(transformation).length) { 
      transformation.format = 'jpg'
    }
    void getAndSetUrl([transformation]).then(url => {
      setImageUrl(url)
    })
  }, [selectedFile, getFileUrlDynamicArguments])

  function getOriginalCoords(value: number): number {
    // divide by scaling factor
    const scalingFactorValue = scalingFactor.current ?? 1
    return Number(Math.round(value / scalingFactorValue))
  }

  function getCanvasCoords(value: number): number {
    // multiply by scaling factor
    const scalingFactorValue = scalingFactor.current ?? 1
    return Number(Math.round(value * scalingFactorValue))
  }

  useEffect(() => {
    const defaultCC = {
      'x-coordinate': renderedBgImageWidth
        ? Math.round(renderedBgImageWidth / 4)
        : 20,
      'y-coordinate': renderedBgImageHeight
        ? Math.round(renderedBgImageHeight / 4)
        : 20,
      width: renderedBgImageWidth ? Math.round(renderedBgImageWidth / 2) : 100,
      height: renderedBgImageHeight
        ? Math.round(renderedBgImageHeight / 2)
        : 100,
    }
    setInitialCC(defaultCC)
  }, [renderedBgImageHeight, renderedBgImageWidth])

  async function setInitialValues() {
    const customCoordinatesString = selectedFile.customCoordinates ?? ''
    const customCoordinatesArr = selectedFile.customCoordinates
      ? customCoordinatesString.split(',')
      : []

    if (customCoordinatesArr.length) {
      const coordsObj = {
        'x-coordinate': getCanvasCoords(Number(customCoordinatesArr[0])),
        'y-coordinate': getCanvasCoords(Number(customCoordinatesArr[1])),
        width: getCanvasCoords(Number(customCoordinatesArr[2])),
        height: getCanvasCoords(Number(customCoordinatesArr[3])),
      }

      setCC(coordsObj)
      // create link
      await getAndSetUrl([
        {
          width: customCoordinatesArr[2],
          height: customCoordinatesArr[3],
          focus: 'custom',
          cropMode: 'extract',
        },
      ]).then(url => void setCroppedImageUrl(url))

      setChangedCustomCoordinates({
        'x-coordinate': Number(customCoordinatesArr[0]),
        'y-coordinate': Number(customCoordinatesArr[1]),
        width: Number(customCoordinatesArr[2]),
        height: Number(customCoordinatesArr[3]),
      })

      return coordsObj
    }

    const ccToUpdate = {
      'x-coordinate': renderedBgImageWidth
        ? Math.round(renderedBgImageWidth / 4)
        : initialCC['x-coordinate'],
      'y-coordinate': renderedBgImageHeight
        ? Math.round(renderedBgImageHeight / 4)
        : initialCC['y-coordinate'],
      width: renderedBgImageWidth
        ? Math.round(renderedBgImageWidth / 2)
        : initialCC.width,
      height: renderedBgImageHeight
        ? Math.round(renderedBgImageHeight / 2)
        : initialCC.height,
    }

    setCC(ccToUpdate)

    return emptyCCObj
  }

  useEffect(() => {
    if (scalingFactor.current) {
      void setInitialValues()
    }
  }, [
    selectedFile,
    getFileUrlDynamicArguments,
    renderedBgImageWidth,
    renderedBgImageHeight,
    imageScaledInfo,
  ])

  function setRectCoords(obj, coords) {
    if (obj && canvasRef.current) {
      fabricUtils.setObjCoords(obj, coords, canvasRef.current)
    }
  }

  function updateCCValue(coords) {
    if (coords.width && coords.height) {
      const ccToUpdate = {
        'x-coordinate': getOriginalCoords(coords['x-coordinate']),
        'y-coordinate': getOriginalCoords(coords['y-coordinate']),
        width: getOriginalCoords(coords.width),
        height: getOriginalCoords(coords.height),
      }
      setChangedCustomCoordinates(ccToUpdate)
    }
  }

  async function setBgImage() {
    try {
      const bgInfo = await fabricUtils.setCanvasBackgroundImage(
        selectedFile.fileId,
        canvasRef.current,
        imageUrl,
        canvasWidth,
        canvasHeight,
        true,
        null,
        [],
        selectedFile.updatedAt,
      )
      setImageLoaded(true)
      setRenderedBgImageHeight(bgInfo?.renderedBgImageHeight)
      setRenderedBgImageWidth(bgInfo?.renderedBgImageWidth)
      setImageScaledInfo(bgInfo?.imageScaledInfo)
    } catch (err) {
      // console.log(err)
    }
  }

  useEffect(() => {
    canvas = new window.fabric.Canvas('cc-editor-canvas', { stateful: true })
    canvasRef.current = canvas

    if (containerRef.current && tableRef.current) {
      canvasWidth =
        containerRef.current.clientWidth - tableRef.current.clientWidth - 20 // 20px padding
    }

    canvas.on('object:moved', e => {
      const obj = e.target
      fabricUtils.constrainMovedObjToBoundingRect(obj)
      preventCanvasCCDoubleUpdate.current = true

      const currentCoords = fabricUtils.fetchCoords(obj)

      updateCCValue(currentCoords)
    })

    canvas.on('object:scaled', e => {
      const obj = e.target
      fabricUtils.constrainScaledObjToBoundingRect(obj)
      preventCanvasCCDoubleUpdate.current = true
      updateCCValue(fabricUtils.fetchCoords(obj))
    })

    return () => {
      canvas.dispose()
    }
  }, [])

  useEffect(() => {
    if (!isImageLoaded) {
      loadingText.current = fabricUtils.addText(
        canvasRef.current,
        'Loading...',
        {
          fontFamily: 'Roboto',
          top: canvasHeight / 2,
          left: canvasWidth / 3,
          selectable: false,
        },
      )
    } else {
      fabricUtils.removeObject(canvasRef.current, loadingText.current)
    }
  }, [isImageLoaded])

  useEffect(() => {
    void setBgImage()
  }, [imageUrl])

  function addCropBox() {
    if (rectObject.current) {
      return
    }

    updateCCValue(canvasCC)

    if (canvasCC.width && canvasCC.height) {
      rectObject.current = fabricUtils.addRectangle(canvasRef.current, {
        width: canvasCC.width,
        height: canvasCC.height,
        left: canvasCC['x-coordinate'],
        top: canvasCC['y-coordinate'],
        hasBorder: false,
        stroke: 'white',
        strokeWidth: 1,
        hasRotatingPoint: false,
        fill: 'rgba(0,0,0,0.3)',
        strokeUniform: true,
      })
    }
  }

  useEffect(() => {
    if (scalingFactor.current && isEditing) {
      addCropBox()
    } else if (rectObject.current) {
      fabricUtils.removeObject(canvasRef.current, rectObject.current)
      rectObject.current = undefined
    }
  }, [imageScaledInfo, isEditing, canvasCC])

  useEffect(() => {
    if (rectObject.current) {
      setRectCoords(rectObject.current, {
        left: canvasCC['x-coordinate'],
        top: canvasCC['y-coordinate'],
        width: canvasCC.width,
        height: canvasCC.height,
      })
    }
  }, [canvasCC, canvasRef.current, rectObject.current])

  useEffect(() => {
    if (!preventCanvasCCDoubleUpdate.current && scalingFactor.current) {
      setCC({
        'x-coordinate':
          changedCustomCoordinates['x-coordinate'] === 0
            ? initialCC['x-coordinate']
            : getCanvasCoords(changedCustomCoordinates['x-coordinate']),
        'y-coordinate':
          changedCustomCoordinates['y-coordinate'] === 0
            ? initialCC['y-coordinate']
            : getCanvasCoords(changedCustomCoordinates['y-coordinate']),
        width:
          changedCustomCoordinates.width === 0
            ? initialCC.width
            : getCanvasCoords(changedCustomCoordinates.width),
        height:
          changedCustomCoordinates.height === 0
            ? initialCC.height
            : getCanvasCoords(changedCustomCoordinates.height),
      })
    } else {
      setCroppedImageUrl('') // empty saved cropped url
      preventCanvasCCDoubleUpdate.current = false
    }
  }, [changedCustomCoordinates, imageScaledInfo])

  function resetCustomCoordinates() {
    const emptyChangedCustomCoordinates = {
      'x-coordinate': 0,
      'y-coordinate': 0,
      width: 0,
      height: 0,
    }
    setChangedCustomCoordinates(emptyChangedCustomCoordinates)
    setCroppedImageUrl('')
  }

  return (
    <Flex ref={containerRef} justifyContent="space-around" margin="5px 0px">
      <Flex
        flex={2}
        justifyContent="center"
        alignItems="center"
        margin="16px 0px"
        minHeight="600px"
        backgroundColor="white"
        bgGradient="linear( 45deg,
          #efefef 25%,
          transparent 25%,
          transparent 75%,
          #efefef 75%,
          #efefef),linear-gradient(
            45deg,
            #efefef 25%,
            transparent 25%,
            transparent 75%,
            #efefef 75%,
            #efefef
          )"
        backgroundPosition="0 0, 10px 10px"
        backgroundSize="21px 21px"
        padding={5}
      >
        <canvas className="cc-editor-canvas" id="cc-editor-canvas" />
      </Flex>
      <Flex ref={tableRef} flex={1} paddingLeft="20px">
        {isEditing ? (
          <Box>
            <Flex justifyContent="end" marginBottom="2px">
              <span
                role="button"
                tabIndex={0}
                className="reset-link"
                onClick={() => {
                  resetCustomCoordinates()
                }}
                onKeyDown={() => {
                  resetCustomCoordinates()
                }}
              >
                Reset
              </span>
            </Flex>
            <CustomCoordinatesTable
              changedCustomCoordinates={changedCustomCoordinates}
              setChangedCustomCoordinates={setChangedCustomCoordinates}
              file={selectedFile}
              onClose={onClose}
              getFileDetails={getFileDetails}
            />
            <Box marginTop="10px">
              {croppedImageUrl ? (
                <Link href={croppedImageUrl} rel="noreferrer" isExternal>
                  Example URL
                </Link>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box marginTop="16px">
            <ChakraText>
              Draw a custom focus area on the image and crop around it using{' '}
              <code>fo-custom</code> parameter in the URL.{' '}
              <Link
                href="https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations#example-focus-using-custom-coordinates"
                isExternal
              >
                Learn more
              </Link>
              .
            </ChakraText>

            <Button
              marginTop="5px"
              onClick={() => {
                showEditOptions(true)
                // App.trackEvent('CustomFocusDrawInitiated')
              }}
            >
              Draw custom focus area
            </Button>
          </Box>
        )}
      </Flex>
    </Flex>
  )
}

export default CCEditor
